// path: src/services/api.js
//
// Single place for all frontend API calls.
// - Uses REACT_APP_API_URL if defined (e.g. "/api" in production)
// - Otherwise falls back to same-origin "/api"
// - Works with JSON and FormData
// - Throws rich Errors with status + payload for React Query

// ---------- Base URL ----------
const BASE_URL =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_API_URL) ||
  `${window.location.origin.replace(/\/$/, '')}/api`;

// ---------- Core HTTP helper ----------
async function http(method, path, { params, body, headers } = {}) {
  const url = new URL(`${BASE_URL}${path}`);

  // attach query params
  if (params && typeof params === 'object') {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, v);
      }
    }
  }

  // headers
  const finalHeaders = {
    Accept: 'application/json',
    ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...(headers || {}),
  };

  const res = await fetch(url.toString(), {
    method,
    headers: finalHeaders,
    body: body
      ? body instanceof FormData
        ? body
        : JSON.stringify(body)
      : undefined,
    credentials: 'same-origin',
  });

  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const err = new Error((isJson && (data?.error || data?.message)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return { data, status: res.status };
}

// ---------- Auth helper (for admin endpoints) ----------
function authHeader() {
  const token = localStorage.getItem('token'); // adapt if you store it elsewhere
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------- PUBLIC APIs (storefront) ----------
export const productsAPI = {
  // GET /api/products?search=&category=&page=&limit=
  getAll: (args = {}) => http('GET', '/products', { params: args }),

  // GET /api/products/featured?limit=8
  getFeatured: (limit = 8) => http('GET', '/products/featured', { params: { limit } }),

  // GET /api/products/:id
  getById: (id) => http('GET', `/products/${id}`),

  // GET /api/products?category=:categoryId&limit=:limit
  getByCategory: (categoryId, opts = {}) =>
    http('GET', '/products', { params: { ...opts, category: categoryId } }),
};

export const categoriesAPI = {
  // GET /api/categories  (public)
  getAll: () => http('GET', '/categories'),
};

export const newsletterAPI = {
  subscribe: (email) => http('POST', '/newsletter/subscribe', { body: { email } }),
  listSubscribers: (args = {}) => http('GET', '/newsletter/subscribers', { params: args, headers: authHeader() }),
  deleteSubscriber: (id) => http('DELETE', `/newsletter/subscribers/${id}`, { headers: authHeader() }),
  sendNewsletter: (payload) => http('POST', '/newsletter/send', { body: payload, headers: authHeader() })
};

// ---------- Optional: ADMIN APIs (dashboard) ----------
export const adminAPI = {
  products: {
    // GET /api/admin/products
    list: (args = {}) =>
      http('GET', '/admin/products', {
        params: args,
        headers: authHeader(),
      }),

    // POST /api/admin/products
    create: (payload) =>
      http('POST', '/admin/products', {
        body: payload,
        headers: authHeader(),
      }),

    // PUT /api/admin/products/:id
    update: (id, payload) =>
      http('PUT', `/admin/products/${id}`, {
        body: payload,
        headers: authHeader(),
      }),

    // DELETE /api/admin/products/:id
    remove: (id) =>
      http('DELETE', `/admin/products/${id}`, {
        headers: authHeader(),
      }),
  },

  categories: {
    // GET /api/admin/categories
    list: () =>
      http('GET', '/admin/categories', {
        headers: authHeader(),
      }),

    // POST /api/admin/categories
    create: (payload) =>
      http('POST', '/admin/categories', {
        body: payload,
        headers: authHeader(),
      }),

    // PUT /api/admin/categories/:id
    update: (id, payload) =>
      http('PUT', `/admin/categories/${id}`, {
        body: payload,
        headers: authHeader(),
      }),

    // DELETE /api/admin/categories/:id
    remove: (id) =>
      http('DELETE', `/admin/categories/${id}`, {
        headers: authHeader(),
      }),
  },

  analytics: {
    getVisitStats: (params = {}) =>
      http('GET', '/analytics/visits', {
        params,
        headers: authHeader(),
      }),
  },
};

// ---------- Export base URL for debugging ----------
export const __API_BASE__ = BASE_URL;

// ---------- Analytics (public) ----------
export const analyticsAPI = {
  trackVisit: (payload = {}) => http('POST', '/analytics/visit', { body: payload }),
};
